/**
 * Node Metadata Tooltip Component
 *
 * Displays additional metadata from the schema in a tooltip.
 * Uses a portal to render outside ReactFlow's viewport to avoid clipping.
 * Appears after a short delay when hovering over a node.
 */
import { memo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type {
  ExternalService,
  InternalElement,
  Source,
  Sink,
  BoundaryGate,
  SmartAssetBoundary,
  ValueFlowsLayoutAnnotation,
} from "../../../../types/valueFlows";

type OriginalDataType =
  | ExternalService
  | InternalElement
  | Source
  | Sink
  | BoundaryGate
  | SmartAssetBoundary
  | ValueFlowsLayoutAnnotation;

interface NodeMetadataTooltipProps {
  /** Original schema data for the node */
  originalData?: OriginalDataType;
  /** Node category for type-specific rendering */
  nodeCategory: "external" | "source" | "internal" | "gate" | "sink";
  /** Additional config from node data */
  config?: Record<string, unknown>;
  /** Allocation percentage if present */
  allocation?: string;
  /** Whether hover is active */
  isHovered: boolean;
  /** Delay before showing tooltip (ms) */
  delay?: number;
}

interface MetadataItemProps {
  label: string;
  value: string | number;
}

const MetadataItem = ({ label, value }: MetadataItemProps) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="text-sa-text-muted">{label}:</span>
    <span className="text-sa-text font-mono">{value}</span>
  </div>
);

export const NodeMetadataTooltip = memo(
  ({
    originalData,
    nodeCategory,
    config,
    allocation,
    isHovered,
    delay = 500,
  }: NodeMetadataTooltipProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
    const parentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (isHovered) {
        const timer = setTimeout(() => setShowTooltip(true), delay);
        return () => clearTimeout(timer);
      } else {
        setShowTooltip(false);
      }
    }, [isHovered, delay]);

    useEffect(() => {
      if (!showTooltip || !parentRef.current) {
        setCoords(null);
        return;
      }

      const nodeWrapper = parentRef.current.closest("[data-testid^='value-flow-node-']");
      if (!nodeWrapper) {
        setCoords(null);
        return;
      }

      const rect = nodeWrapper.getBoundingClientRect();
      setCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
      });
    }, [showTooltip]);

    // Hidden ref element to find parent node
    const refElement = <div ref={parentRef} className="absolute w-0 h-0 pointer-events-none" />;

    if (!originalData) {
      return refElement;
    }

    // Extract metadata based on node category
    const metadataItems: Array<{ label: string; value: string | number }> = [];

    // Show node type from category (capitalize first letter)
    const typeLabel = nodeCategory.charAt(0).toUpperCase() + nodeCategory.slice(1);
    metadataItems.push({ label: "Type", value: typeLabel });

    // Allocation
    if (allocation) {
      metadataItems.push({ label: "Allocation", value: allocation });
    }

    // Gate-specific: multisig config
    if (nodeCategory === "gate" && config) {
      const multisigN = config.multisig_n as number | undefined;
      const multisigM = config.multisig_m as number | undefined;
      const timeout = config.approval_timeout_s as number | undefined;

      if (multisigN && multisigM) {
        metadataItems.push({ label: "Signatures", value: `${multisigM} of ${multisigN}` });
      }
      if (timeout) {
        const hours = Math.round(timeout / 3600);
        metadataItems.push({ label: "Timeout", value: `${hours}h` });
      }

      const signerRoles = config.signer_roles as string[] | undefined;
      if (signerRoles?.length) {
        metadataItems.push({ label: "Roles", value: signerRoles.join(", ") });
      }
    }

    // Source-specific
    if (nodeCategory === "source") {
      const sourceType = (originalData as Source).source_type;
      if (sourceType) {
        metadataItems.push({ label: "Source Type", value: sourceType.replace(/_/g, " ") });
      }
    }

    // Sink-specific
    if (nodeCategory === "sink") {
      const sinkType = (originalData as Sink).sink_type;
      if (sinkType) {
        metadataItems.push({ label: "Sink Type", value: sinkType.replace(/_/g, " ") });
      }
    }

    // Position hint
    const position = (originalData as { position?: string }).position;
    if (position && position !== "internal") {
      metadataItems.push({ label: "Position", value: position.replace(/_/g, " ") });
    }

    if (metadataItems.length === 0 || !showTooltip || !coords) {
      return refElement;
    }

    const tooltip = (
      <div
        style={{
          position: "fixed",
          left: coords.x,
          top: coords.y,
          transform: "translate(-50%, -100%)",
          zIndex: 9999,
        }}
        className={`
          pointer-events-none
          bg-sa-surface/95
          border border-sa-border
          rounded-lg shadow-xl
          px-3 py-2
          min-w-[160px] max-w-[240px]
          animate-in fade-in-0 zoom-in-95 duration-150
        `}
      >
        <div className="text-[10px] uppercase tracking-wide text-sa-text-muted mb-1.5 font-semibold">
          Details
        </div>
        <div className="space-y-1">
          {metadataItems.map((item) => (
            <MetadataItem key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      </div>
    );

    return (
      <>
        {refElement}
        {createPortal(tooltip, document.body)}
      </>
    );
  },
);

NodeMetadataTooltip.displayName = "NodeMetadataTooltip";
